/**
 * Main-thread API for an RTL-SDR source.
 *
 * Topology:
 *   [Main thread]
 *     ├─ requestDevice() (user gesture)
 *     ├─ creates SAB ring (IQ)
 *     ├─ spawns USB Worker  ──── writes IQ ──► SAB
 *     └─ spawns DSP Worker  ◄─── reads IQ ──── SAB
 *                            ──► postMessage(FFT frame) ──► main
 *
 * The main thread doesn't see raw IQ; it only receives FFT frames (for
 * spectrum/waterfall) and lightweight stats (sample counts). The two workers
 * run in parallel and don't compete with the UI for CPU.
 *
 * Underlying driver: @jtarrio/webrtlsdr (Apache-2.0).
 */

import { SabRing } from '../ring/sab-ring';
import type { DemodMode } from '../../workers/dsp-worker';

const RTL2832U_FILTERS: USBDeviceFilter[] = [
  { vendorId: 0x0bda, productId: 0x2832 },
  { vendorId: 0x0bda, productId: 0x2838 },
];

const DEFAULT_CHUNK_SAMPLES = 65_536;
/** SAB ring capacity (bytes). At 2.4 MS/s × 2 bytes/sample = 4.8 MB/s, 8 MB gives ~1.7 s buffer. */
const IQ_RING_CAPACITY_BYTES = 8 * 1024 * 1024;
const STATS_INTERVAL_MS = 250;

export type RtlSdrStatus = 'idle' | 'connecting' | 'connected' | 'streaming' | 'closing' | 'error';

export type StreamOptions = {
  sampleRate: number;
  centerFreq: number;
  gain: number | null;
  /** FFT bin count (power of two, 64..16384). Default 2048. */
  fftSize?: number;
  /** Target rate of FFT frames delivered to listeners (Hz). Default 30. */
  fftRateHz?: number;
  /** Demodulator mode the DSP worker should run on this stream. */
  mode: DemodMode;
  /** Channel filter bandwidth in Hz (used by NFM/AM, ignored by WFM). */
  bandwidthHz: number;
  /** Software offset-tuning shift in Hz. Physical LO = centerFreq + offsetHz. */
  offsetHz?: number;
  /** Crystal correction in PPM. */
  ppmCorrection?: number;
  /** Bias-T toggle (powers external LNAs through the coax). */
  biasT?: boolean;
  /** Direct-sampling mode for HF reception below ~24 MHz. */
  directSampling?: 'off' | 'i' | 'q';
  /** WFM audio de-emphasis time constant in microseconds. 50 (ITU R1 /
   *  Europe) or 75 (Americas / Japan). Default 50. */
  deemphasisUs?: number;
  /** Initial squelch threshold (dBFS) for NFM / AM. Pass ≤ -120 to
   *  disable. Default disabled. */
  squelchDb?: number;
  /** Audio AGC on at startup. Default off. */
  agcOn?: boolean;
  /**
   * SharedArrayBuffer the DSP worker writes 48 kHz mono f32 PCM into,
   * to be consumed by an AudioWorklet on the main thread.
   */
  audioRing: SharedArrayBuffer;
};

export type StatsEvent = {
  /** Bytes of IQ written by the USB worker (cumulative across the stream). */
  bytesWritten: number;
  /** Bytes dropped because the SAB ring was full. */
  bytesDropped: number;
  /** performance.now() timestamp at which stats were sampled. */
  time: number;
};

export type FftEvent = {
  /** Log-magnitude per bin in dBFS, length = fftSize, fftshifted (DC at centre). */
  bins: Float32Array;
  /** performance.now() timestamp of when the FFT was computed in the DSP worker. */
  time: number;
};

export type AudioEvent = {
  /** Interleaved L,R Float32 samples at 48 kHz. Transferable, owned by callback. */
  samples: Float32Array;
  time: number;
};

export type CwTextEvent = {
  text: string;
  /** Estimated words-per-minute from the decoder. */
  wpm: number;
};

export type RdsEvent = {
  synced: boolean;
  pi: number;
  /** Program Service name. Always 8 ASCII chars (space-padded). */
  ps: string;
  /** Radio Text — up to 64 ASCII chars. May contain trailing spaces. */
  rt: string;
  /** Stereo lock state, since the worker has it handy too. */
  stereo: boolean;
};

export type AdsbFramesEvent = {
  /** JSON-encoded array of {df, icao, t, raw} records. */
  framesJson: string;
};

export type StatsCallback = (evt: StatsEvent) => void;
export type FftCallback = (evt: FftEvent) => void;
export type AudioCallback = (evt: AudioEvent) => void;
export type CwTextCallback = (evt: CwTextEvent) => void;
export type RdsCallback = (evt: RdsEvent) => void;
export type AdsbFramesCallback = (evt: AdsbFramesEvent) => void;

// USB-worker outbound message shapes (mirror usb-worker.ts).
type UsbStarted = { kind: 'started'; actualSampleRate: number; actualFrequency: number };
type UsbStats = { kind: 'stats'; bytesWritten: number; bytesDropped: number; time: number };
type UsbStopped = { kind: 'stopped' };
type UsbErr = { kind: 'error'; message: string };
type UsbOutbound = UsbStarted | UsbStats | UsbStopped | UsbErr;

// DSP-worker outbound message shapes (mirror dsp-worker.ts).
type DspReady = { kind: 'ready' };
type DspFft = { kind: 'fft'; bins: Float32Array; time: number };
type DspAudio = { kind: 'audio'; samples: Float32Array; time: number };
type DspCwText = { kind: 'cwText'; text: string; wpm: number };
type DspRds = {
  kind: 'rds';
  synced: boolean;
  pi: number;
  ps: string;
  rt: string;
  stereo: boolean;
};
type DspAdsb = { kind: 'adsbFrames'; framesJson: string };
type DspSquelch = { kind: 'squelch'; open: boolean };
type DspIq = { kind: 'iq'; samples: Uint8Array; time: number };
type DspErr = { kind: 'error'; message: string };
type DspOutbound =
  | DspReady
  | DspFft
  | DspAudio
  | DspCwText
  | DspRds
  | DspAdsb
  | DspSquelch
  | DspIq
  | DspErr;

export type SquelchEvent = { open: boolean };
export type SquelchCallback = (evt: SquelchEvent) => void;
export type IqSamplesEvent = { samples: Uint8Array; time: number };
export type IqSamplesCallback = (evt: IqSamplesEvent) => void;

export class RtlSdrSource {
  private usbWorker: Worker | null = null;
  private dspWorker: Worker | null = null;
  private ring: SabRing | null = null;
  private device: USBDevice | null = null;

  private startResolver: { resolve: () => void; reject: (err: Error) => void } | null = null;
  private stopResolver: { resolve: () => void } | null = null;

  private statsListeners = new Set<StatsCallback>();
  private fftListeners = new Set<FftCallback>();
  private audioListeners = new Set<AudioCallback>();
  private cwTextListeners = new Set<CwTextCallback>();
  private rdsListeners = new Set<RdsCallback>();
  private adsbListeners = new Set<AdsbFramesCallback>();
  private squelchListeners = new Set<SquelchCallback>();
  private iqSamplesListeners = new Set<IqSamplesCallback>();

  /**
   * Opens a device via the WebUSB picker. Must be called inside a user-gesture
   * handler (button click). The chosen device is recorded; the actual USB
   * `open` + configure happens inside the USB worker on `start()`.
   */
  async connect(): Promise<void> {
    if (this.device) return;
    if (!('usb' in navigator)) {
      throw new Error('WebUSB is not available in this browser. Use Chrome, Edge, or Brave.');
    }
    this.device = await navigator.usb.requestDevice({ filters: RTL2832U_FILTERS });
  }

  /** Spawns both workers, gives them the SAB ring, and starts streaming. */
  async start(opts: StreamOptions): Promise<void> {
    if (!this.device) throw new Error('connect() before start()');

    this.ring = SabRing.create(IQ_RING_CAPACITY_BYTES);

    const fftSize = opts.fftSize ?? 2048;
    const fftRateHz = opts.fftRateHz ?? 30;

    this.spawnWorkers();

    const startPromise = new Promise<void>((resolve, reject) => {
      this.startResolver = { resolve, reject };
    });

    // DSP worker boots first so it's ready to drain the ring as soon as USB
    // worker starts writing.
    this.dspWorker!.postMessage({
      kind: 'init',
      iqRing: this.ring.buffer,
      audioRing: opts.audioRing,
      fftSize,
      postRateHz: fftRateHz,
      mode: opts.mode,
      bandwidthHz: opts.bandwidthHz,
      sampleRate: opts.sampleRate,
      deemphasisUs: opts.deemphasisUs,
      squelchDb: opts.squelchDb,
      agcOn: opts.agcOn,
    });

    this.usbWorker!.postMessage({
      kind: 'init',
      vendorId: this.device.vendorId,
      productId: this.device.productId,
      serialNumber: this.device.serialNumber,
      sampleRate: opts.sampleRate,
      centerFreq: opts.centerFreq,
      gain: opts.gain,
      chunkSamples: DEFAULT_CHUNK_SAMPLES,
      iqRing: this.ring.buffer,
      statsIntervalMs: STATS_INTERVAL_MS,
      offsetHz: opts.offsetHz ?? 0,
      ppmCorrection: opts.ppmCorrection,
      biasT: opts.biasT,
      directSampling: opts.directSampling,
    });

    return startPromise;
  }

  /**
   * Retune the device without restarting the stream. Pass any subset of the
   * mutable parameters. Safe to call rapidly (e.g. from a drag handler) —
   * the worker queues them in postMessage order.
   */
  retune(opts: { centerFreq?: number; gain?: number | null; offsetHz?: number }): void {
    if (!this.usbWorker) return;
    this.usbWorker.postMessage({
      kind: 'retune',
      ...opts,
    });
  }

  /**
   * Push advanced device settings (PPM, bias-T, direct sampling) to the
   * USB worker without restarting the stream.
   */
  setAdvanced(opts: {
    ppmCorrection?: number;
    biasT?: boolean;
    directSampling?: 'off' | 'i' | 'q';
  }): void {
    if (!this.usbWorker) return;
    this.usbWorker.postMessage({ kind: 'advanced', ...opts });
  }

  /**
   * Switch the demodulator without restarting the stream. The DSP worker
   * disposes the old demod and constructs a new one in-place; the IQ ring
   * is unaffected so audio resumes within a frame.
   */
  setMode(mode: DemodMode, bandwidthHz: number): void {
    if (!this.dspWorker) return;
    this.dspWorker.postMessage({ kind: 'setMode', mode, bandwidthHz });
  }

  /**
   * Toggle whether the DSP worker forwards demodulated audio batches to
   * the main thread (for recording).
   */
  setRecording(on: boolean): void {
    if (!this.dspWorker) return;
    this.dspWorker.postMessage({ kind: 'setRecording', on });
  }

  /**
   * Change the WFM de-emphasis time constant live. 50 µs (Europe) or
   * 75 µs (Americas). Applies immediately if the current mode is WFM;
   * otherwise the value is held until the next WFM mode entry.
   */
  setDeemphasis(us: number): void {
    if (!this.dspWorker) return;
    this.dspWorker.postMessage({ kind: 'setDeemphasis', us });
  }

  /** Squelch threshold in dBFS for NFM / AM. Pass ≤ -120 to disable. */
  setSquelch(db: number): void {
    if (!this.dspWorker) return;
    this.dspWorker.postMessage({ kind: 'setSquelch', db });
  }

  /** Toggle the post-demod audio AGC. */
  setAgc(on: boolean): void {
    if (!this.dspWorker) return;
    this.dspWorker.postMessage({ kind: 'setAgc', on });
  }

  /** Toggle whether the DSP worker streams raw IQ chunks back to main
   *  for capture. Off by default; very high traffic when on. */
  setIqRecording(on: boolean): void {
    if (!this.dspWorker) return;
    this.dspWorker.postMessage({ kind: 'setIqRecording', on });
  }

  /** Stops streaming. Device remains permitted but the workers shut down. */
  async stop(): Promise<void> {
    if (!this.usbWorker) return;
    const stopPromise = new Promise<void>((resolve) => {
      this.stopResolver = { resolve };
    });
    this.usbWorker.postMessage({ kind: 'stop' });
    this.dspWorker?.postMessage({ kind: 'stop' });
    return stopPromise;
  }

  async disconnect(): Promise<void> {
    if (this.usbWorker) {
      this.usbWorker.postMessage({ kind: 'close' });
      this.usbWorker.terminate();
      this.usbWorker = null;
    }
    if (this.dspWorker) {
      this.dspWorker.postMessage({ kind: 'stop' });
      this.dspWorker.terminate();
      this.dspWorker = null;
    }
    this.ring = null;
    this.device = null;
    this.startResolver = null;
    this.stopResolver = null;
  }

  onStats(cb: StatsCallback): () => void {
    this.statsListeners.add(cb);
    return () => {
      this.statsListeners.delete(cb);
    };
  }

  onFft(cb: FftCallback): () => void {
    this.fftListeners.add(cb);
    return () => {
      this.fftListeners.delete(cb);
    };
  }

  onAudio(cb: AudioCallback): () => void {
    this.audioListeners.add(cb);
    return () => {
      this.audioListeners.delete(cb);
    };
  }

  onCwText(cb: CwTextCallback): () => void {
    this.cwTextListeners.add(cb);
    return () => {
      this.cwTextListeners.delete(cb);
    };
  }

  onRds(cb: RdsCallback): () => void {
    this.rdsListeners.add(cb);
    return () => {
      this.rdsListeners.delete(cb);
    };
  }

  onAdsbFrames(cb: AdsbFramesCallback): () => void {
    this.adsbListeners.add(cb);
    return () => {
      this.adsbListeners.delete(cb);
    };
  }

  onSquelch(cb: SquelchCallback): () => void {
    this.squelchListeners.add(cb);
    return () => {
      this.squelchListeners.delete(cb);
    };
  }

  onIqSamples(cb: IqSamplesCallback): () => void {
    this.iqSamplesListeners.add(cb);
    return () => {
      this.iqSamplesListeners.delete(cb);
    };
  }

  private spawnWorkers(): void {
    if (!this.usbWorker) {
      this.usbWorker = new Worker(new URL('../../workers/usb-worker.ts', import.meta.url), {
        type: 'module',
      });
      this.usbWorker.onmessage = (e: MessageEvent<UsbOutbound>) => this.handleUsb(e.data);
      this.usbWorker.onerror = (e) => {
        const msg = e.message || 'USB worker crashed';
        this.startResolver?.reject(new Error(msg));
        this.startResolver = null;
      };
    }
    if (!this.dspWorker) {
      this.dspWorker = new Worker(new URL('../../workers/dsp-worker.ts', import.meta.url), {
        type: 'module',
      });
      this.dspWorker.onmessage = (e: MessageEvent<DspOutbound>) => this.handleDsp(e.data);
      this.dspWorker.onerror = (e) => {
        const msg = e.message || 'DSP worker crashed';
        this.startResolver?.reject(new Error(msg));
        this.startResolver = null;
      };
    }
  }

  private handleUsb(msg: UsbOutbound): void {
    switch (msg.kind) {
      case 'started':
        this.startResolver?.resolve();
        this.startResolver = null;
        break;
      case 'stats':
        for (const cb of this.statsListeners) {
          cb({ bytesWritten: msg.bytesWritten, bytesDropped: msg.bytesDropped, time: msg.time });
        }
        break;
      case 'stopped':
        this.stopResolver?.resolve();
        this.stopResolver = null;
        break;
      case 'error':
        this.startResolver?.reject(new Error(msg.message));
        this.startResolver = null;
        break;
    }
  }

  private handleDsp(msg: DspOutbound): void {
    switch (msg.kind) {
      case 'ready':
        // DSP is up; USB worker can start writing whenever it wants.
        break;
      case 'fft':
        for (const cb of this.fftListeners) {
          cb({ bins: msg.bins, time: msg.time });
        }
        break;
      case 'audio':
        for (const cb of this.audioListeners) {
          cb({ samples: msg.samples, time: msg.time });
        }
        break;
      case 'cwText':
        for (const cb of this.cwTextListeners) {
          cb({ text: msg.text, wpm: msg.wpm });
        }
        break;
      case 'rds':
        for (const cb of this.rdsListeners) {
          cb({
            synced: msg.synced,
            pi: msg.pi,
            ps: msg.ps,
            rt: msg.rt,
            stereo: msg.stereo,
          });
        }
        break;
      case 'adsbFrames':
        for (const cb of this.adsbListeners) {
          cb({ framesJson: msg.framesJson });
        }
        break;
      case 'squelch':
        for (const cb of this.squelchListeners) {
          cb({ open: msg.open });
        }
        break;
      case 'iq':
        for (const cb of this.iqSamplesListeners) {
          cb({ samples: msg.samples, time: msg.time });
        }
        break;
      case 'error':
        // Surface via the same error path as USB worker errors.
        this.startResolver?.reject(new Error(msg.message));
        this.startResolver = null;
        break;
    }
  }
}
